import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import jsPDF from 'jspdf';
import * as React from 'react';
import * as Redux from 'react-redux';
import { dateformat, formatDNI } from '../../../helpers';
import { getFilesArchivos } from '../../archivos/HelpersArchivos';
import { STUDENT_CONST } from '../../auth/login/LoginReducer';
import { allCarnetAction } from '../../carnet/CarnetAction';
import { captureElementToCanvasHelper } from '../../carnet/CarnetHelper';
import { ACTIVO_CARNET, PARAMS_CARNET } from '../../carnet/CarnetReducer';
import FrontalCarnet from '../../carnet/components/FrontalCarnet';
import LayoutCarnet from '../../carnet/components/LayoutCarnet';
import ReverseCarnet from '../../carnet/components/ReserveCarnet';
import { ALTO_CARNET, ANCHO_CARNET, PDF_A4 } from '../../carnet/ConstCarnet';
import BotonComponent from '../../dashboard/components/BotonNuevo';
import CodeQrComponent from '../../usuario/usu_entidades/components/code_qr';

// Constantes para la configuración del QR en el PDF
const QR_CONFIG = {
  imageQuality: 1.0, // Calidad máxima para QR
  qrSize: 100, // Tamaño del QR
  columns: 4, // Columnas por página
  rows: 5, // Filas por página
  marginX: 30, // Margen horizontal
  marginY: 25, // Margen vertical
  padding: 7.5, // Padding interno
  boxWidth: 130, // Ancho del recuadro
  boxHeight: 135, // Alto del recuadro
  fontSize: {
    id: 9, // Tamaño de fuente para ID
    name: 9, // Tamaño de fuente para nombre
  },
};

const TodosCarnetsEstudiantes = ({ tipoCarnet }) => {
  const dispatch = Redux.useDispatch();

  const estudiantesStore = Redux.useSelector((state) => state.estudiantes);
  const entidadesStore = Redux.useSelector((state) => state.entidades);
  const carnetStore = Redux.useSelector((state) => state.carnet);

  const [loading, setLoading] = React.useState(false);
  const [tipoBoton, setTipoBoton] = React.useState(0);
  const [error, setError] = React.useState('');
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [loadingQr, setLoadingQr] = React.useState(false);

  const [frontalRefs, setFrontalRef] = React.useState({});
  const [reverseRefs, setReverseRef] = React.useState({});
  const [qrRefs, setQrRef] = React.useState({});

  const ajustes = entidadesStore.ajustes;

  const values =
    estudiantesStore.selects.length > 0
      ? estudiantesStore.all.filter((el) =>
          estudiantesStore.selects.includes(el.id),
        )
      : estudiantesStore.all;

  React.useEffect(() => {
    async function fetchData() {
      const payload = { idEntidad: entidadesStore.activo.id };
      dispatch({ type: PARAMS_CARNET, payload });
      const res = await allCarnetAction(dispatch, payload);

      // Buscar el diseño de carnet para estudiantes
      const estudianteDiseno = res.data.find((el) => {
        const { perfil } = el;
        return perfil && perfil.rol === STUDENT_CONST;
      });

      if (res.data.length > 0) {
        dispatch({ type: ACTIVO_CARNET, payload: estudianteDiseno });
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCompleto = async () => {
    setLoading(true);
    let doc = new jsPDF('portrait', 'pt', PDF_A4);
    let count = 0;
    let procesados = 0;
    let fallidos = 0;

    try {
      for (const el of values) {
        // Verificar si el usuario tiene avatar y las referencias existen
        if (!el.usuario.perfil.avatar) {
          fallidos++;
          continue;
        }

        // Verificar referencias
        const frontalRef = frontalRefs[el.id];
        const reverseRef = reverseRefs[`reversa${el.id}`];

        if (!frontalRef?.current || !reverseRef?.current) {
          console.error(`Referencias no disponibles para usuario ID ${el.id}`);
          fallidos++;
          continue;
        }

        try {
          const canvasFront = await captureElementToCanvasHelper(frontalRef);
          const dataCanvaFront = canvasFront.toDataURL('image/jpeg', 1.0); // Máxima calidad

          const canvasReverse = await captureElementToCanvasHelper(reverseRef);
          const dataCanvaReverse = canvasReverse.toDataURL('image/jpeg', 1.0);

          const x = 100;
          const y = 25 + count * 247.4;

          doc.addImage(dataCanvaFront, 'JPEG', x, y, ANCHO_CARNET, ALTO_CARNET);
          doc.addImage(
            dataCanvaReverse,
            'JPEG',
            x + 160.4,
            y,
            ANCHO_CARNET,
            ALTO_CARNET,
          );

          count++;
          procesados++;

          if (count === 3) {
            doc.addPage();
            count = 0;
          }
        } catch (err) {
          console.error(`Error procesando carnet de usuario ID ${el.id}:`, err);
          fallidos++;
        }
      }

      // Si no se procesó ningún carnet, mostrar error
      if (procesados === 0) {
        throw new Error('No se pudo procesar ningún carnet');
      }

      doc.save(`PDF-CARNET-ESTUDIANTES-${procesados}.pdf`);

      // Mostrar mensaje si hubo errores parciales
      if (fallidos > 0) {
        setError(
          `Se generaron ${procesados} carnets. No se pudieron procesar ${fallidos} carnets.`,
        );
        setOpenSnackbar(true);
      }
    } catch (error) {
      setError('Error al generar PDF completo: ' + error.message);
      setOpenSnackbar(true);
      setTipoBoton(0);
    } finally {
      setLoading(false);
      setTipoBoton(0);
    }
  };

  const handleSoloFront = async () => {
    setLoading(true);
    setTipoBoton(2);
    let doc = new jsPDF('portrait', 'pt', PDF_A4);

    let countRow = 0;
    let countColumn = 0;
    let procesados = 0;
    let fallidos = 0;

    try {
      for (const el of values) {
        // Verificar si el usuario tiene avatar y la referencia existe
        if (!el.usuario.perfil.avatar) {
          fallidos++;
          continue;
        }

        // Verificar referencia frontal
        const frontalRef = frontalRefs[el.id];
        if (!frontalRef?.current) {
          console.error(
            `Referencia frontal no disponible para usuario ID ${el.id}`,
          );
          fallidos++;
          continue;
        }

        try {
          const canvasFront = await captureElementToCanvasHelper(frontalRef);
          const dataCanvaFront = canvasFront.toDataURL('image/jpeg', 1.0);

          const x = 40 + countColumn * 157.31;
          const y = 25 + countRow * 247.82;

          doc.addImage(dataCanvaFront, 'JPEG', x, y, ANCHO_CARNET, ALTO_CARNET);

          countColumn++;
          procesados++;

          if (countColumn === 3) {
            countColumn = 0;
            countRow++;
          }

          if (countRow === 3) {
            doc.addPage();
            countRow = 0;
          }
        } catch (err) {
          console.error(
            `Error procesando frontal de usuario ID ${el.id}:`,
            err,
          );
          fallidos++;
        }
      }

      // Si no se procesó ningún carnet, mostrar error
      if (procesados === 0) {
        throw new Error('No se pudo procesar ningún carnet frontal');
      }

      doc.save(`PDF-CARNET-SOLO-FRONTAL-${procesados}.pdf`);

      // Mostrar mensaje si hubo errores parciales
      if (fallidos > 0) {
        setError(
          `Se generaron ${procesados} carnets frontales. No se pudieron procesar ${fallidos} carnets.`,
        );
        setOpenSnackbar(true);
      }

      setLoading(false);
      setTipoBoton(0);
    } catch (error) {
      setError('Error al generar PDF frontal: ' + error.message);
      setOpenSnackbar(true);
      setLoading(false);
      setTipoBoton(0);
    }
  };

  const handleSoloQR = async () => {
    setLoading(true);
    setTipoBoton(3);

    // Crear documento PDF
    let doc = new jsPDF('portrait', 'pt', PDF_A4);
    let countRow = 0;
    let countColumn = 0;
    let qrProcesados = 0;

    try {
      // Primero validamos que todos los QRs tengan un valor válido
      const validQRs = values.filter(
        (el) => el.usuario.codeqr !== null && qrRefs[`codeqr${el.id}`]?.current,
      );

      if (validQRs.length === 0) {
        throw new Error('No hay códigos QR válidos para generar');
      }

      // Configuración de fuente para todo el documento
      doc.setFont('helvetica');

      // Procesamos cada estudiante
      for (const el of values) {
        // Validación para asegurarnos que el código QR y la referencia existan
        if (!el.usuario.codeqr) {
          continue;
        }

        const codeqrRef = qrRefs[`codeqr${el.id}`];
        if (!codeqrRef?.current) {
          console.error(`Referencia QR no disponible para usuario ID ${el.id}`);
          continue;
        }

        try {
          // Calcular posición en la página
          const x = QR_CONFIG.marginX + countColumn * QR_CONFIG.boxWidth;
          const y = QR_CONFIG.marginY + countRow * QR_CONFIG.boxHeight;

          // Capturar y convertir el QR a imagen
          const canvasQR = await captureElementToCanvasHelper(codeqrRef);
          const dataCanvaQR = canvasQR.toDataURL(
            'image/jpeg',
            QR_CONFIG.imageQuality,
          );

          // Dibujar recuadro
          doc.setDrawColor(0);
          doc.setLineWidth(0.5);
          doc.rect(x, y, QR_CONFIG.boxWidth, QR_CONFIG.boxHeight);

          // Añadir imagen QR
          doc.addImage(
            dataCanvaQR,
            'JPEG',
            x + QR_CONFIG.padding + 8,
            y + QR_CONFIG.padding,
            QR_CONFIG.qrSize,
            QR_CONFIG.qrSize,
          );

          // Añadir información de texto (ID)
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(QR_CONFIG.fontSize.id);
          const idText = `${
            el.usuario.perfil.tipoDni
          }-${el.usuario.perfil.dni.trim()}`;
          doc.text(
            idText,
            x + QR_CONFIG.boxWidth / 2,
            y + QR_CONFIG.qrSize + QR_CONFIG.padding + 10,
            { align: 'center' },
          );

          // Añadir nombre
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(QR_CONFIG.fontSize.name);
          const nameText =
            el.usuario.perfil.pnombre.trim() +
            ' ' +
            el.usuario.perfil.papellido.trim();
          doc.text(
            nameText,
            x + QR_CONFIG.boxWidth / 2,
            y + QR_CONFIG.qrSize + QR_CONFIG.padding + 20,
            { align: 'center' },
          );

          // Control de layout
          countColumn++;
          if (countColumn === QR_CONFIG.columns) {
            countColumn = 0;
            countRow++;
          }

          if (countRow === QR_CONFIG.rows) {
            doc.addPage();
            countRow = 0;
          }

          qrProcesados++;
        } catch (err) {
          console.error(`Error procesando QR de usuario ID ${el.id}:`, err);
        }
      }

      // Si no se procesó ningún QR, mostrar error
      if (qrProcesados === 0) {
        throw new Error('No se pudo procesar ningún código QR');
      }

      // Guardar documento
      doc.save(`PDF-ESTUDIANTES-CODIGOS-QR-${qrProcesados}.pdf`);
      setLoading(false);
      setTipoBoton(0);
    } catch (error) {
      setError('Error al generar PDF de códigos QR: ' + error.message);
      setOpenSnackbar(true);
      setLoading(false);
      setTipoBoton(0);
    }
  };

  function setDataCanvas() {
    setLoading(true);
    for (const el of values) {
      setFrontalRef((prevState) => ({
        ...prevState,
        [el.id]: React.createRef(),
      }));
      setReverseRef((prevState) => ({
        ...prevState,
        [`reversa${el.id}`]: React.createRef(),
      }));
      setQrRef((prevState) => ({
        ...prevState,
        [`codeqr${el.id}`]: React.createRef(),
      }));
    }
    setLoading(false);
  }

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const botonFull =
    tipoCarnet === 'qrCode' && Object.keys(frontalRefs).length > 0 ? null : (
      <BotonComponent
        text={
          Object.keys(frontalRefs).length > 0
            ? 'Descargar Carnets completo'
            : 'Generar carnet'
        }
        click={async () => {
          setLoadingQr(true);

          setTipoBoton(1);
          if (Object.keys(frontalRefs).length === 0) {
            setDataCanvas();

            setTimeout(() => {
              setLoadingQr(false);
            }, 1000);

            return;
          }

          await handleCompleto();
        }}
        disabled={loading || loadingQr}
        loading={loading || loadingQr}
        green={Object.keys(frontalRefs).length === 0}
        blue={Object.keys(frontalRefs).length > 0}
        mr={2}
      />
    );

  const botonSoloFrontal =
    tipoCarnet === 'qrCode' ? null : (
      <BotonComponent
        text="Descargar Solo frontal"
        click={async () => {
          setTipoBoton(2);
          if (Object.keys(frontalRefs).length === 0) {
            setDataCanvas(2);
            return;
          }

          await handleSoloFront();
        }}
        disabled={loading}
        loading={loading && tipoBoton === 2}
        green={Object.keys(frontalRefs).length === 0}
        blue={Object.keys(frontalRefs).length > 0}
        mr={2}
      />
    );

  const botonSoloQR =
    tipoCarnet !== 'qrCode' ? null : (
      <BotonComponent
        text="Descargar Solo Código QR"
        click={async () => {
          setTipoBoton(3);
          if (Object.keys(qrRefs).length === 0) {
            setDataCanvas();
            return;
          }

          await handleSoloQR();
        }}
        disabled={loading}
        loading={loading && tipoBoton === 3}
        green={Object.keys(qrRefs).length === 0}
        blue={Object.keys(qrRefs).length > 0}
      />
    );

  if (!carnetStore.activo && tipoCarnet !== 'qrCode') {
    return <h3>Falta el diseños del carnet</h3>;
  }

  return (
    <Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Grid container spacing={2}>
        <Grid item sx={12} sm={12}>
          {botonFull}
          {Object.keys(frontalRefs).length > 0 && botonSoloFrontal}
          {Object.keys(qrRefs).length > 0 && botonSoloQR}
          <br />
          <strong>Cantidad seleccionados:</strong> {values.length}
          {'   '}
          {tipoCarnet !== 'qrCode' && (
            <>
              <strong>Total de hojas:</strong> {Math.ceil(values.length / 3)}
              <br />
              <strong>Total de códigos QR:</strong> {values.length}
              {tipoCarnet === 'qrCode' && (
                <>
                  <strong>Total de hojas QR:</strong>{' '}
                  {Math.ceil(
                    values.length / (QR_CONFIG.columns * QR_CONFIG.rows),
                  )}
                </>
              )}
            </>
          )}
        </Grid>

        {tipoCarnet === 'qrCode' ? (
          loadingQr ? (
            <Grid item sx={12} sm={12}>
              <CircularProgress size="3rem" sx={{ mt: 4 }} />
            </Grid>
          ) : (
            values.map((el) => {
              return (
                <Grid
                  item
                  sx={12}
                  sm={2}
                  key={`carnet_${el.id}`}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <CodeQrComponent
                    qrRef={qrRefs[`codeqr${el.id}`]}
                    size={90}
                    maxWidth={120}
                    value={el.usuario.codeqr}
                  />
                  <Typography
                    variant="body"
                    fontWeight="bold"
                    textAlign="center"
                  >
                    {el.usuario.perfil.pnombre} {el.usuario.perfil.papellido}
                  </Typography>
                </Grid>
              );
            })
          )
        ) : (
          <Grid item sx={12} sm={12}>
            {carnetStore.loadingAll ? (
              <center>
                <CircularProgress size="3rem" sx={{ mt: 4 }} />
              </center>
            ) : (
              values.map((el) => {
                const seccion = el.matricula.secciones.find(
                  (sec) => !sec.aprobado,
                )?.seccion;

                let nivel = `${seccion.nivelAcademico.nivelAcademico.abrev}`;
                let seccionName = `${seccion.nombre}`;
                let carrera = seccion.carrera
                  ? `${seccion.carrera?.carrera?.nombre}`
                  : null;

                let parametro1Text = '';
                if (carnetStore.activo.parametro1) {
                  parametro1Text = carnetStore.activo.parametro1
                    .replace('$NI', seccion.nivelAcademico.nivelAcademico.abrev)
                    .replace('$CA', carrera)
                    .replace('$SE', seccionName);
                }

                return (
                  <div key={`carnet_${el.id}`} style={{ marginBottom: '30px' }}>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                        }}
                      >
                        {fullnameCarnet(el.usuario)}
                      </Grid>
                      <Grid item sx={12} sm={9}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <LayoutCarnet
                            cardRef={frontalRefs[el.id]}
                            bg={getFilesArchivos(carnetStore.activo.fondo)}
                          >
                            <FrontalCarnet
                              notEdit
                              data={{
                                titleFullnameText: 'APELLIDOS Y NOMBRES',
                                photo: getFilesArchivos(
                                  el.usuario.perfil.avatar,
                                ),
                                titleText: carnetStore.activo.titleText,
                                subtitleText: carnetStore.activo.subtitleText,
                                titleSeccionText:
                                  carnetStore.activo.titleSeccionText,
                                titleNivelText:
                                  carnetStore.activo.titleNivelText,
                                ...getFullnames(el.usuario.perfil),
                                dni: `${
                                  el.usuario.perfil.tipoDni
                                } - ${formatDNI(el.usuario.perfil.dni.trim())}`,
                                periodo: `PERÍODO ${ajustes.periodoAcademico.desde}-${ajustes.periodoAcademico.hasta}`,
                                nivel,
                                seccion: seccionName,
                                carrera,
                                profile: 'ESTUDIANTE',
                                titleBackText: carnetStore.activo.titleBackText,
                                titleAgeText: carnetStore.activo.titleAgeText,
                                parametro1: parametro1Text,
                                parametro2: carnetStore.activo.parametro2,
                                subtitleBackText:
                                  carnetStore.activo.subtitleBackText,
                                age: dateformat(
                                  el.usuario.perfil.fechaNacimiento,
                                  { inverse: true },
                                ),
                              }}
                            />
                          </LayoutCarnet>
                          <LayoutCarnet
                            cardRef={reverseRefs[`reversa${el.id}`]}
                            bg={getFilesArchivos(carnetStore.activo.fondoBack)}
                            isReverse
                          >
                            <ReverseCarnet
                              qrRef={qrRefs[`codeqr${el.id}`]}
                              data={{
                                titleBackText: carnetStore.activo.titleBackText,
                                subtitleBackText:
                                  carnetStore.activo.subtitleBackText,
                                titleFirmaText:
                                  carnetStore.activo.titleFirmaText,
                              }}
                              valueCodeqr={el.usuario.codeqr}
                              notEdit
                            />
                          </LayoutCarnet>
                        </Stack>
                      </Grid>
                    </Grid>
                  </div>
                );
              })
            )}
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default TodosCarnetsEstudiantes;

export function getFullnames(perfil) {
  const { pnombre, snombre, papellido, sapellido } = perfil;
  const names = `${pnombre.trim()}${snombre ? ` ${snombre.trim()}` : ''}`;
  const surnames = `${papellido.trim()}${
    sapellido ? ` ${sapellido.trim()}` : ''
  }`;

  return { names, surnames };
}

export const fullnameCarnet = (usuario) => {
  return (
    <Typography
      variant="body"
      fontWeight="bold"
      color="initial"
      sx={{ minWidth: '300px' }}
    >
      {usuario.perfil.tipoDni} - {formatDNI(usuario.perfil.dni.trim())}
      <br />
      {usuario.perfil.pnombre}
      {usuario.perfil.snombre && ` ${usuario.perfil.snombre}`}
      <br />
      {usuario.perfil.papellido}
      {usuario.perfil.sapellido && ` ${usuario.perfil.sapellido}`}
    </Typography>
  );
};
